// Opens a restaurant for the signed-in agent: the one path the support picker and
// the sidebar switcher share.
//
// Key export: `useOpenRestaurant`.
// Neighbours: `components/SupportPicker.tsx`, `components/RestaurantGrid.tsx`,
// `components/SwitchRestaurantDialog.tsx`; `lib/recentRestaurants.ts` records each open.
//
// An admin or support agent gets a just-in-time support grant, then the team is
// selected (which releases the previous grant). A merchant with several teams of
// their own only selects.

import { useCallback, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { APP_LANDING_PATH } from '@/shell/navRegistry'
import { useTeam } from '@/state/useTeam'
import { useAdminStatus } from '@/state/useAdminStatus'
import * as keycast from '@/lib/keycastClient'
import { recordOpenedTeam } from '@/lib/recentRestaurants'

export function useOpenRestaurant(onOpened?: () => void) {
  const navigate = useNavigate()
  const { teams, addSupportSession, selectTeam } = useTeam()
  const adminRole = useAdminStatus((s) => s.role)
  const [opening, setOpening] = useState<number | null>(null)
  const [error, setError] = useState<string | null>(null)

  const open = useCallback(
    async (tid: number) => {
      setOpening(tid)
      setError(null)
      try {
        recordOpenedTeam(tid)
        if (adminRole !== null) {
          const res = await keycast.grantSupportAccess(tid)
          await addSupportSession({
            teamId: tid,
            teamName: teams.find((t) => t.id === tid)?.name ?? 'Restaurant',
            storedKeyPubkey: res.stored_key_pubkey,
            bunkerUrl: res.bunker_url,
          })
        } else {
          await selectTeam(tid)
        }
        onOpened?.()
        navigate(APP_LANDING_PATH)
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to open restaurant')
      } finally {
        setOpening(null)
      }
    },
    [adminRole, addSupportSession, navigate, onOpened, selectTeam, teams],
  )

  return { open, opening, error }
}
